import {Avatar, SimpleDropdown} from '@di-nxt/components';
import React, {useCallback, useMemo} from 'react';
import {t} from '../../localization';
import {useFavouriteMembers, useTeamMembers} from '../../services';
import {MemberDropdownProps} from './types';

export const MemberDropdown: React.FC<MemberDropdownProps> = ({
  teamId,
  selectedMemberId,
  onMemberSelected,
}) => {
  // Get members
  const members = useTeamMembers(teamId);

  // Map to list of display names
  const memberItems = useMemo(
    () =>
      members
        .sort((a, b) =>
          ((a.lastName ?? '') + '' + (a.firstName ?? '')).localeCompare(
            (b.lastName ?? '') + '' + (b.firstName ?? ''),
          ),
        )
        .map(m => m.display),
    [members],
  );

  // Get favourites
  const {favouriteMembers, addFavouriteMember} = useFavouriteMembers();
  const favouriteMemberIndicies = useMemo(() => {
    return favouriteMembers
      .map(id => members.find(m => m.membershipId === id))
      .filter(m => m !== undefined)
      .map(m => members.findIndex(m2 => m2.membershipId === m?.membershipId));
  }, [favouriteMembers, members]);

  // Handlers
  const handleSelection = useCallback(
    (index: number | undefined) => {
      if (onMemberSelected) {
        if (index !== undefined) {
          // Add to top of favourites
          addFavouriteMember(members[index].membershipId);
          onMemberSelected(members[index].membershipId);
        } else {
          onMemberSelected(undefined);
        }
      }
    },
    [addFavouriteMember, members, onMemberSelected],
  );

  const onRenderBadge = useCallback(
    (index: number): React.ReactElement | null => {
      return (
        <Avatar
          name={members[index].display}
          shape="round"
          size="24"
          uri={members[index].avatarUrl}
        />
      );
    },
    [members],
  );

  return (
    <SimpleDropdown
      readonly={false}
      items={memberItems}
      favourites={favouriteMemberIndicies}
      onRenderBadge={onRenderBadge}
      onSelectedIndexChange={handleSelection}
      selectedIndex={members.findIndex(
        m => m.membershipId === selectedMemberId,
      )}
      placeholder={t('components.memberdropdown.placeholder')}
    />
  );
};
